import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/Button";
import { OrderTotals } from "../../OrderTotals";
import { boxTotals } from "../../helpers";
import { ItemCard } from "../../SubProductCard";
import { BoxDrawerPageProps } from "../../RootBoxDrawer";
import { productSelect } from "@/store";
import {
  getDisplayDate,
  droppsProductToProductCardProduct,
} from "../../helpers";
import { PortalDrawer } from "..";
import {
  useSelectedItemContext,
  useProcessCharge,
  useFetchSubscriptions,
} from "../../SelectedItemContext";
import { fetchChargesByID, fetchCharges } from "../../RechargeApiActions";
import {
  OrderProcessedPageProps,
  OrderProcessedPage,
} from "./OrderProcessedPage";
import { addMonthsToDate } from "@/helpers/utils";

type OrderNowPageProps = BoxDrawerPageProps;

type OrderNowPageDrawerProps = OrderProcessedPageProps & {
  page: "process-order";
};

const OrderNowPageDrawer = ({ page, box, onExit }: OrderNowPageDrawerProps) => {
  return page === "process-order" ? (
    <OrderProcessedPage box={box} onExit={onExit} />
  ) : null;
};

const OrderNowPage = ({ onExit, box, notify }: OrderNowPageProps) => {
  const { rechargeItem } = useSelectedItemContext().boxItem;
  const currentChargeDate = rechargeItem.nextChargeDate;
  const [openedDrawer, setOpenedDrawer] = useState<"process-order" | undefined>(
    undefined
  );
  const [laoding, setLoading] = useState<boolean>(false);
  const chargeProcessor = useProcessCharge();
  const fetchSubscriptionProcessor = useFetchSubscriptions();
  const chargeIdRef = useRef<null | number>(null);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const charges = await fetchCharges({ subscriptionId: rechargeItem.id });
        if (charges.length > 0) {
          chargeIdRef.current = charges[0].id;
        }
      } catch (error) {
        notify({
          title: "Error",
          severity: "error",
          message: "Error on fetching charges",
        });
      }
    };
    fetchData();
  }, [notify, rechargeItem]);
  if (!box) return null;
  const { items, discount, address } = box;
  const isAllOneTime = !items.some(
    (boxItem) => boxItem.rechargeItem.status === "ACTIVE"
  );
  const totals = boxTotals(items, discount);
  const discountInfo =
    totals.discounts !== undefined && discount
      ? { amount: totals.discounts, name: discount.name }
      : undefined;

  const checkSubscriptionUpdate = async (chargeId: number) => {
    try {
      const charges = await fetchChargesByID({
        subscriptionId: rechargeItem.id,
        chargeID: chargeId,
      });
      if (charges.length > 0) {
        return charges[0];
      }
    } catch (error) {
      notify({
        title: "Error",
        severity: "error",
        message: "Error on fetching charge by ID",
      });
    }
  };

  const pollSubscriptionUpdate = async (chargeId: number) => {
    let attempts = 0;
    const maxAttempts = 10;
    const delay = (attempts: number) => Math.pow(2, attempts) * 1000;

    while (attempts < maxAttempts) {
      try {
        const chargeData = await checkSubscriptionUpdate(chargeId);

        if (chargeData?.status === "SUCCESS") {
          // Perform the next action, e.g., navigate to the order processed screen
          setOpenedDrawer("process-order");
          setLoading(false);
          return;
        } else if (chargeData?.status === "ERROR") {
          handleOrderProcessExit();
          setLoading(false);
          notify({
            title: "Error - Please refresh the page.",
            message: chargeData.error,
            severity: "error",
          });
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, delay(attempts)));
        attempts++;
      } catch (error) {
        console.error("Polling error:", error);
        throw error;
      }
    }
  };

  const processOrder = async () => {
    setLoading(true);
    if (chargeIdRef.current !== null) {
      chargeProcessor.mutate(chargeIdRef.current, {
        onSuccess(data) {
          pollSubscriptionUpdate(chargeIdRef.current!);
        },
        onError() {
          setLoading(false);
          notify({
            title: "Error",
            severity: "error",
            message:
              "Something went wrong. Please refresh the page or try after sometime",
          });
        },
      });
    } else {
      setLoading(false);
      notify({
        title: "Error",
        severity: "error",
        message: "Charge ID is not available. Please try again later.",
      });
    }
  };

  const handleOrderProcessExit = () => {
    fetchSubscriptionProcessor.mutate();
    onExit();
  };
  return (
    <>
      <h3 className="subtitle text-base font-semibold">Order Now</h3>
      <div className="space-y-4">
        {items.map((s) => {
          const { product, variant, rechargeItem } = s;
          const productCardProduct = droppsProductToProductCardProduct({
            product,
            variant,
          });
          return (
            <ItemCard
              key={rechargeItem.id}
              product={productCardProduct}
              price={rechargeItem.price}
              quantity={rechargeItem.quantity}
              shippingInfo={rechargeItem.cadence ?? undefined}
              className="rounded border border-solid border-primary"
            />
          );
        })}
      </div>
      <OrderTotals
        shippingCost={totals.shipping}
        subtotal={totals.lineItemsPrice}
        discount={discountInfo}
        estimatedTotal={totals.estimatedTotal}
      />
      <Button
        className="w-full"
        onClick={processOrder}
        loading={laoding}
        data-portal-button="process-order"
      >
        Process Order
      </Button>
      <div className="w-full space-y-4">
        <p className="body text-sm font-semibold">
          Clicking the button above will process your order immediately.
        </p>
        <p className="ml-5 text-sm">Here’s what will change:</p>
        <ul className="ml-5 text-sm">
          <li className="list-disc">
            Current charge date: {getDisplayDate(currentChargeDate)}{" "}
          </li>
          <li className="list-disc">New charge date: TODAY</li>
          {!isAllOneTime && (
            <li className="list-disc">
              Future charge dates:
              <ul className="ml-5 text-sm">
                {items.map((s) => {
                  const { rechargeItem, product, variant } = s;
                  const productCardProduct = droppsProductToProductCardProduct({
                    product,
                    variant,
                  });
                  const updatedDate = addMonthsToDate(
                    new Date(),
                    rechargeItem.cadence?.frequency || 0
                  );
                  return rechargeItem.status === "ACTIVE" ? (
                    <li
                      key={"future-date" + rechargeItem.id}
                      className="list-disc"
                    >
                      {productSelect.name(productCardProduct)}:{" "}
                      {getDisplayDate(updatedDate)}
                    </li>
                  ) : null;
                })}
              </ul>
            </li>
          )}
        </ul>
        <hr className="divider-x my-auto" />
        <p className="body text-sm italic">
          Your order will ship as soon as possible after the charge is
          processed. In some cases this may be the following day.
        </p>
      </div>
      <PortalDrawer
        open={openedDrawer !== undefined}
        title={"Order Now"}
        contents={() =>
          openedDrawer !== undefined ? (
            <OrderNowPageDrawer
              page={openedDrawer}
              onExit={handleOrderProcessExit}
              box={box}
            />
          ) : null
        }
        goToFirstPage={() => {}}
        onExit={handleOrderProcessExit}
        notify={() => {}}
        transparentBackdrop={true}
      />
    </>
  );
};

export { OrderNowPage };
